<script>
    import { onMount, onDestroy } from "svelte";
    import hotspotLogo from "../assets/img/hotspot.png";
    import visaLogo from "../assets/img/visa.png";
    import mastercardLogo from "../assets/img/mastercard.png";
    import nedbankLogo from "../assets/img/nedbank.png";
    import standardBankLogo from "../assets/img/standardbank.png";
    import absaLogo from "../assets/img/absa.png";
    import fnbLogo from "../assets/img/fnb.png";
    import capitecLogo from "../assets/img/capitec.png";
    import back from "../assets/img/back.png";
    import Error from "./components/Error.svelte";
    import { getHotspotData } from "./utils.svelte.js";
    import { requestJSON, safeCheckoutURL, savePendingCheckout } from "./payment.js";

    export let store;

    const baseURL = import.meta.env.VITE_BASE_URL;
    const portalURL = import.meta.env.VITE_BASE_PORTAL_URL;

    let errorMessage = null;
    let errorTimeout = null;
    let purchasingProductID = null;
    let hotspotBundles = [];
    let catalogueLoading = true;
    let purchaseController = null;
    let catalogueController = null;
    let navigating = false;
    let disposed = false;

    async function loadCatalogue() {
        catalogueController?.abort();
        catalogueController = new AbortController();
        catalogueLoading = true;
        errorMessage = null;
        try {
            const products = await requestJSON(`${baseURL}products/`, {
                signal: catalogueController.signal, timeoutMs: 10000,
            });
            if (!Array.isArray(products) || products.length === 0 ||
                products.some(product => !Number.isSafeInteger(product.id) ||
                    !product.name || !Number.isFinite(Number(product.price)) || Number(product.price) <= 0)) {
                throw new Error("Hotspot bundles are currently unavailable.");
            }
            if (!disposed) hotspotBundles = products;
        } catch (error) {
            if (!disposed) errorMessage = error.message || "Could not load bundles. Please try again.";
        } finally {
            if (!disposed) catalogueLoading = false;
        }
    }

    function showError(message) {
        errorMessage = message;
        if (errorTimeout) {
            clearTimeout(errorTimeout);
        }
        errorTimeout = setTimeout(() => {
            errorMessage = null;
        }, 3000);
    }

    async function redirectToPayment(productID) {
        if (disposed || purchasingProductID !== null) {
            return;
        }

        errorMessage = null;
        purchasingProductID = productID;
        purchaseController = new AbortController();
        const controller = purchaseController;

        try {
            // Refresh the router identity at purchase time, not just on page
            // load: the customer may have roamed to another hotspot meanwhile.
            await getHotspotData();
            if (controller.signal.aborted || disposed) return;

            if (!store.macAddress || !store.isConnectedToHotspot) {
                showError(
                    "We could not identify this device. Reconnect to the hotspot and try again",
                );
                return;
            }
            if (!/^0\d{9}$/.test(store.cellphone || "")) {
                showError("Please sign in before purchasing a hotspot bundle");
                return;
            }

            const data = await requestJSON(`${portalURL}card/payment/`, {
                method: "POST",
                signal: controller.signal,
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    payment_channel: "ion",
                    hotspot_product: String(productID),
                    expected_amount: hotspotBundles.find(product => product.id === productID)?.price,
                    mac_address: store.macAddress,
                    origin_nas_identifier: store.node || null,
                    cellphone: store.cellphone,
                }),
            });

            if (controller.signal.aborted || disposed) return;
            if (!safeCheckoutURL(data?.checkout_url)) {
                showError("Payment could not be started. Please try again");
                return;
            }
            savePendingCheckout({ id: data.checkout_id, token: data.checkout_token });
            navigating = true;
            window.location.assign(data.checkout_url);
        } catch (error) {
            navigating = false;
            if (!disposed && !controller.signal.aborted) {
                if (error.status === 400 &&
                    error.message === "Please sign in on this hotspot device before purchasing.") {
                    store.setPinLoginNotice("Enter your PIN to sign in on this hotspot device before purchasing.");
                    store.setState("PIN_LOGIN");
                    return;
                }
                if (error.status === 409) await loadCatalogue();
                showError(error.name === "TimeoutError"
                    ? "Payment request timed out. Please try again"
                    : error.message || "Payment failed. Please try again");
            }
        } finally {
            if (!navigating && purchaseController === controller) purchasingProductID = null;
        }
    }

    function backButton() {
        if (navigating) return;
        disposed = true;
        purchaseController?.abort();
        store.setState("ACCOUNT");
    }

    function handlePageShow(event) {
        if (!event.persisted || !navigating || disposed) return;
        // Browser Back can restore the purchase view with its navigation lock.
        // Account recovers the existing checkout instead of starting another one.
        disposed = true;
        navigating = false;
        purchasingProductID = null;
        store.setState("ACCOUNT");
    }

    onMount(loadCatalogue);
    onMount(() => {
        window.addEventListener("pageshow", handlePageShow);
        return () => window.removeEventListener("pageshow", handlePageShow);
    });
    onDestroy(() => {
        disposed = true;
        purchaseController?.abort();
        catalogueController?.abort();
        clearTimeout(errorTimeout);
    });
</script>

<div class="w-full px-2 py-8">
    <div class="w-full mx-4">
        <button class="flex flex-row" on:click={backButton} disabled={navigating}>
            <img class="w-3" src={back} alt="Back button" />
            <div class="w-1"></div>
            <p class="text-[#5b95c8] text-md font-bold mx-2">Back</p>
        </button>
    </div>

    <div class="h-2"></div>

    <div class="mx-auto w-2/5">
        <img src={hotspotLogo} alt="TooMuchWifi hotspot logo" />
    </div>

    <div class="justify-center w-full">
        <div class="mx-auto">
            <p class="font-medium mb-2 py-4 text-center text-xl">
                Purchase Hotspot Package
            </p>
        </div>

        {#if errorMessage}
            <div class="w-full max-w-sm mx-auto mb-4">
                <Error message={errorMessage} />
            </div>
        {/if}

        {#if catalogueLoading}
            <p class="text-center" role="status">Loading hotspot bundles...</p>
        {:else if hotspotBundles.length === 0}
            <div class="text-center">
                <button class="text-[#1899d1]" on:click={loadCatalogue}>Try loading bundles again</button>
            </div>
        {/if}

        <div class="h-1/6"></div>

        <div class="flex flex-wrap justify-center items-end space-y-4 mb-4">
            {#each hotspotBundles as product}
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <div class="flex flex-col mx-2 w-5/12 max-w-60">
                    <div class="bg-black py-2 flex-col rounded-t-2xl">
                        <h2 class="font-medium mx-4 text-2xl text-white">
                            {product.name}
                        </h2>
                        <div class="h-2"></div>
                        <div class="bg-yellow-500 h-0.5 mx-2 mt-auto"></div>
                    </div>

                    <div class="bg-black flex-row h-85 relative">
                        <div class="h-14"></div>
                        <h2
                            class="text-2xl lg:text-4xl text-white font-bold ml-4 uppercase top-0 left-0 absolute"
                        >
                            R{product.price}
                        </h2>
                        <div
                            class="absolute border-[#1899d1] bottom-0 right-0 bg-white h-10 w-7/12 rounded-tl-full border-t-8 border-l-8"
                        ></div>
                        <div class="bg-[#1899d1] h-2"></div>
                    </div>

                    <div class="bg-white rounded-b-2xl py-4 shadow-xl">
                        <div class="mx-auto w-fit">
                            <button
                                on:click={() => redirectToPayment(product.id)}
                                disabled={purchasingProductID !== null}
                                class="bg-[#1899d1] text-white px-4 py-1 text-center text-xs font-semibold rounded-sm"
                            >
                                {purchasingProductID === product.id
                                    ? "Opening payment..."
                                    : "Buy Now"}
                            </button>
                        </div>
                    </div>
                </div>
            {/each}
        </div>
    </div>
</div>
